"use client";

import Image from "next/image";
import { ExternalLink, CheckCircle } from "lucide-react";
import { trackCouponClick } from "@/app/actions";
import { usageLabel } from "@/lib/coupon-signals";
import { formatHistoryDate } from "@/lib/verification-history";
import type { CardHistory } from "@/lib/client-props";
import CouponVerificationHistory from "./CouponVerificationHistory";
import CheckDate from "./CheckDate";
import { offerFactsLine, type OfferFacts } from "@/lib/offer-facts";
import { getCouponBadge } from "@/lib/coupon-badge";
import "./store/store-v5.css";

interface CouponCardProps {
  id: string;
  title: string;
  code?: string;
  affiliateLink: string;
  storeSlug: string;
  storeName: string;
  storeId: string;
  description?: string;
  expiry?: string;
  verified?: boolean;
  successRate?: number;
  usedToday?: number;
  onOpen?: () => void;
  showViewStoreLink?: boolean;
  storeLogo?: string;
  type?: string;
  isFavorite?: boolean;
  curatorName?: string;
  isExclusive?: boolean;
  discountValue?: number | null;
  discountType?: "PERCENT" | "FIXED" | "FREE_SHIPPING" | "DEAL" | null;
  /** Public verification history for this coupon (built on the server from real runs and votes). */
  history?: CardHistory;
  /** Set on the "recently expired" list: ISO date the offer expired, shown as "Expired Sep 28". */
  expiredAt?: string;
  /** ISO time of a failed latest check of a still-live code (SEO R3 rule 6): "Didn't work when we last checked · {date}". */
 lastCheckFailedAt?: string | null;
  /** ISO time of the newest VALID run of a code labelled "Didn't work when we last checked": "Last worked {date}" replaces "Used N times". null / absent = nothing in that slot. */
  lastWorkedAt?: string | null;
  /** Offer facts row (lib/offer-facts): "Applies to: ... · For: ... · Billing: ...". Absent / empty = no row. */
  facts?: OfferFacts | null;
  /** Set on the "recently expired" list for an offer without a real expiry date: listed as "Expired", no date shown. */
  expired?: boolean;
  /** The page the card sits on, for the new-tab URL of "Get Code" (default `/{storeSlug}/coupons`; a REFERRAL page passes its own). */
  pagePath?: string;
}

export default function CouponCard({
  id,
  title,
  affiliateLink,
  storeSlug,
  storeName,
  storeId,
  description,
  expiry,
  // Honest signals: nothing is shown unless the caller passes real data
  // (no "VERIFIED" by default, no default "100% Success", no "Used 0 times today").
  // `verified` is the badge decision made on the server (lib/coupon-ranking): a worked run in the last 90 days or our
  // own code. It is not the DB flag.
  verified = false,
  successRate,
  usedToday,
  showViewStoreLink = false,
  storeLogo,
  type = "CODE",
  isExclusive = false,
  discountValue,
  discountType,
  history,
  expiredAt,
  lastCheckFailedAt,
  lastWorkedAt,
  facts,
  expired = false,
  pagePath,
}: CouponCardProps) {
  const showSuccessRate = typeof successRate === "number";
  // A code that failed its last check does not advertise how often it was used: "Last worked {date}" when a past VALID run exists,
  // otherwise that slot stays empty (audit 2026-10-25 item 2; real data only).
  const usage = lastCheckFailedAt ? null : usageLabel(usedToday);

  const isDeal = type === "DEAL";
  // One small muted row under the offer text; nothing when the offer has no facts (never a heading).
  const factsLine = offerFactsLine(facts);

  const handleGetCode = async () => {
    // Track usage
    trackCouponClick(id);

    // Only used for Home Page flow (redirect current tab)
    if (storeId) {
      setTimeout(() => {
        window.location.href = `/out/${id}`;
      }, 100);
    }
  };

  // Badge figure: never contradicts the heading below (src/lib/coupon-badge.ts).
  const getDisplayDiscount = () => getCouponBadge({ title, description, type, discountType, discountValue });

  const discount = getDisplayDiscount();
  const expiredLabel = expiredAt ? formatHistoryDate(expiredAt) : null;
  const expiredShort = expiredLabel ? expiredLabel.replace(/, \d{4}$/, "") : null;

  return (
    <article id={`offer-${id}`} className={`sp-coupon${expiredAt || expired ? " sp-coupon--expired" : ""}`}>
      <div className="sp-coupon__layout">
        {/* Discount stub */}
        <div className="sp-coupon__discount">
          {isExclusive && <div className="sp-coupon__exclusive">Exclusive</div>}
          <div>
            <strong data-size={discount.value.length > 4 ? "small" : undefined}>{discount.value}</strong>
            {discount.type === 'percent' && <span>OFF</span>}
          </div>
        </div>

        {/* Offer text: the full text, never clamped */}
        <div className="sp-coupon__copy">
          {showViewStoreLink && storeLogo && (
            <Image
              src={storeLogo}
              alt={`${storeName} logo`}
              width={40}
              height={24}
              className="sp-coupon__logo"
              unoptimized={storeLogo.startsWith('http')}
            />
          )}
          {/* No heading element in an offer card: the visible line is a paragraph in the card's title style (SEO audit 2026-10-25,
              finding 6; owner rule: no hidden text). The page outline keeps only the page's own headings. */}
          <p className="sp-coupon__title">{description || title}</p>
          {factsLine && <p className="sp-coupon__facts">{factsLine}</p>}
          <div className="sp-coupon__meta">
            {lastCheckFailedAt && (
              <span className="sp-coupon__failed-check">Didn&apos;t work when we last checked · <CheckDate value={lastCheckFailedAt} /></span>
            )}
            {verified && !lastCheckFailedAt && (
              <span className="sp-coupon__verified" title="Verified Coupon">
                <CheckCircle aria-hidden="true" />
                Verified
              </span>
            )}
            {expiredShort && <span className="sp-coupon__expired-on">Expired {expiredShort}</span>}
            {!expiredShort && expired && <span className="sp-coupon__expired-on">Expired</span>}
            {!expiredShort && !expired && expiry && <span>Expires: {expiry}</span>}
            {showSuccessRate && (
              <span title={`${successRate}% success from shopper votes`}>
                <meter min="0" max="100" value={successRate} className="sr-only">{successRate}%</meter>
                {successRate}% Success
              </span>
            )}
            {usage && <span>{usage}</span>}
            {lastCheckFailedAt && lastWorkedAt && <span className="sp-coupon__last-worked">Last worked <CheckDate value={lastWorkedAt} /></span>}
          </div>
          {showViewStoreLink && (
            <a href={`/${storeSlug}/coupons`} title={`${storeName} Coupons`} className="sp-coupon__store-link">
              View all {storeName} Coupons
              <ExternalLink aria-hidden="true" width={12} height={12} />
            </a>
          )}
        </div>

        {/* Action: reveal (frozen flow); voting lives in the code modal */}
        <div className="sp-coupon__actions">
          <a
            href={`${pagePath ?? `/${storeSlug}/coupons`}#couponId-${id}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleGetCode}
            className="sp-cta"
            data-kind={isDeal ? "deal" : "code"}
          >
            <span>{isDeal ? "Get Deal" : "Get Code"}</span>
          </a>
        </div>
      </div>

      <CouponVerificationHistory history={history} offerKind={isDeal ? "deal" : "code"} />
    </article>
  );
}
